import { useMutation } from '@tanstack/react-query';
import { PRICE_CURRENCIES, PRICE_PERIODS, type Price } from '@lp/contracts';
import { useState } from 'react';
import { errorText, fieldProblems } from '@/lib/api';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Button, Dialog, ErrorBox, Field, Input, Select } from './ui';

const PERIOD_LABEL: Record<Price['period'], string> = { month: 'a month', year: 'a year', once: 'once' };

/** a price as the owner reads it: 300 USD a month */
export function PriceText({ price }: { price: Price | null | undefined }) {
  const t = useT();
  if (!price) return <span className="text-ink-400">—</span>;
  return (
    <>
      {num(price.amount)} {price.currency} <span className="text-ink-500">{t(PERIOD_LABEL[price.period])}</span>
    </>
  );
}

/** set a price (amount typed freely, its currency, how often) — or none / back to the inherited one */
export function PriceDialog({ title, price, clearLabel, onSave, onClose }: { title: string; price: Price | null; clearLabel: string; onSave: (p: Price | null) => Promise<unknown>; onClose: () => void }) {
  const t = useT();
  const [amount, setAmount] = useState(price ? String(price.amount) : '');
  const [currency, setCurrency] = useState<Price['currency']>(price?.currency ?? 'USD');
  const [period, setPeriod] = useState<Price['period']>(price?.period ?? 'month');
  const save = useMutation({ mutationFn: (p: Price | null) => onSave(p), onSuccess: onClose });
  const value = Number(amount);
  const ok = amount.trim() !== '' && Number.isFinite(value) && value >= 0;
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className="mr-auto" onClick={() => save.mutate(null)} disabled={save.isPending}>
            {clearLabel}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate({ amount: value, currency, period })} disabled={!ok || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-3 gap-2">
        <Field label={t('Amount')}>
          <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus aria-label={t('Amount')} />
        </Field>
        <Field label={t('Currency')}>
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Price['currency'])} className="w-full" aria-label={t('Currency')}>
            {PRICE_CURRENCIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('How often')}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value as Price['period'])} className="w-full" aria-label={t('How often')}>
            {PRICE_PERIODS.map((p) => (
              <option key={p} value={p}>
                {t(PERIOD_LABEL[p])}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

/** names in both languages, and a description */
export function NamesDialog({ title, value, withDescription, onSave, onClose }: { title: string; value: { nameEn: string; nameKm: string; description?: string | null }; withDescription?: boolean; onSave: (v: { nameEn: string; nameKm: string; description?: string | null }) => Promise<unknown>; onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({ nameEn: value.nameEn, nameKm: value.nameKm, description: value.description ?? '' });
  const save = useMutation({ mutationFn: () => onSave({ nameEn: f.nameEn.trim(), nameKm: f.nameKm.trim(), ...(withDescription ? { description: f.description.trim() || null } : {}) }), onSuccess: onClose });
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!f.nameEn.trim() || !f.nameKm.trim() || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Name (English)')}>
        <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} maxLength={80} autoFocus />
      </Field>
      <Field label={t('Name (Khmer)')}>
        <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} maxLength={80} />
      </Field>
      {withDescription && (
        <Field label={t('Description')}>
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={400} />
        </Field>
      )}
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}
